import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
/** Tipo mínimo requerido para el combobox de clientes */
interface ClienteBasico {
  id: string | null
  nombre: string | null
  cuit?: number | string | null
}

interface ClienteComboboxProps {
  clientes: ClienteBasico[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  /** Opción para mostrar "Todos los clientes" al inicio (útil para filtros) */
  showAllOption?: {
    value: string
    label: string
  }
  /** Clase CSS para el botón trigger */
  className?: string
}

export function ClienteCombobox({
  clientes,
  value,
  onValueChange,
  placeholder = 'Seleccionar cliente...',
  disabled = false,
  showAllOption,
  className,
}: ClienteComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  // Filtrar clientes válidos
  const clientesValidos = clientes.filter((c) => c.id && c.nombre)

  // Cliente seleccionado (puede ser la opción "todos" o un cliente)
  const isAllSelected = showAllOption && value === showAllOption.value
  const selectedCliente = isAllSelected ? null : clientesValidos.find((cliente) => cliente.id === value)

  // Filtrar clientes por búsqueda
  const filteredClientes = React.useMemo(() => {
    if (!searchQuery) return clientesValidos

    const query = searchQuery.toLowerCase()
    return clientesValidos.filter((cliente) => {
      const nombre = cliente.nombre?.toLowerCase() || ''
      const cuit = cliente.cuit?.toString() || ''

      return nombre.includes(query) || cuit.includes(query)
    })
  }, [clientesValidos, searchQuery])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between", className)}
        >
          {isAllSelected ? (
            <span className="truncate">{showAllOption.label}</span>
          ) : selectedCliente ? (
            <span className="truncate">{selectedCliente.nombre}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre, CUIT..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[250px] overflow-y-auto overscroll-contain">
            <CommandEmpty>
              {searchQuery
                ? 'No se encontraron clientes.'
                : 'No hay clientes disponibles.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Opción "Todos los clientes" si está habilitada y no hay búsqueda */}
              {showAllOption && !searchQuery && (
                <CommandItem
                  value={showAllOption.value}
                  onSelect={() => {
                    onValueChange(showAllOption.value)
                    setOpen(false)
                    setSearchQuery('')
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      isAllSelected ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="font-medium">{showAllOption.label}</span>
                </CommandItem>
              )}
              {filteredClientes.slice(0, 100).map((cliente) => (
                <CommandItem
                  key={cliente.id}
                  value={cliente.id!}
                  onSelect={(currentValue) => {
                    onValueChange(currentValue === value ? '' : currentValue)
                    setOpen(false)
                    setSearchQuery('')
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === cliente.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{cliente.nombre}</span>
                    {cliente.cuit && (
                      <span className="text-xs text-muted-foreground">
                        CUIT: {cliente.cuit}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
              {filteredClientes.length > 100 && (
                <div className="p-2 text-center text-xs text-muted-foreground border-t">
                  Mostrando 100 de {filteredClientes.length} resultados. Refina tu búsqueda.
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
